import React, { useState, useEffect } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import './MethodologyValidationDashboard.css';

const MethodologyValidationDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchValidationData();
  }, []);

  const fetchValidationData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosPrivate.get('/MethodologyValidation/complete');
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load methodology validation');
      console.error('Methodology validation error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="methodology-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Running comprehensive methodology validation...</p>
          <p className="loading-detail">Testing 30 parameter combinations + Feature ablation + GMM clustering</p>
          <p className="loading-time">⏱️ Estimated time: 2-3 minutes</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="methodology-container">
        <div className="error-message">
          <h3>❌ Error Loading Methodology Validation</h3>
          <p>{error}</p>
          <button onClick={fetchValidationData} className="retry-button">🔄 Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="methodology-container">
      {}
      <header className="methodology-header">
        <h1>📊 Methodology Validation Dashboard</h1>
        <p className="subtitle">
          Parameter Optimization · Feature Engineering · Algorithm Comparison
        </p>
      </header>

      {}
      <section className="dataset-info-section">
        <h2>Dataset Information</h2>
        <div className="info-grid-horizontal">
          <div className="info-card-compact">
            <div className="info-label">M&A Transactions</div>
            <div className="info-value-large">{data.datasetSize}</div>
          </div>
          <div className="info-card-compact">
            <div className="info-label">Parameter Combinations Tested</div>
            <div className="info-value-large">{data.gridSearch.totalCombinations}</div>
          </div>
          <div className="info-card-compact">
            <div className="info-label">Feature Ablation Tests</div>
            <div className="info-value-large">{data.featureAblation.totalTests}</div>
          </div>
        </div>
      </section>

      {}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📋 Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'gridSearch' ? 'active' : ''}`}
          onClick={() => setActiveTab('gridSearch')}
        >
          🔍 1. Parameter Optimization
        </button>
        <button
          className={`tab-button ${activeTab === 'ablation' ? 'active' : ''}`}
          onClick={() => setActiveTab('ablation')}
        >
          🧪 2. Feature Engineering
        </button>
        <button
          className={`tab-button ${activeTab === 'algorithmComparison' ? 'active' : ''}`}
          onClick={() => setActiveTab('algorithmComparison')}
        >
          🏆 3. Algorithm Comparison
        </button>
        <button
          className={`tab-button ${activeTab === 'gmm' ? 'active' : ''}`}
          onClick={() => setActiveTab('gmm')}
        >
          🔀 4. GMM Details
        </button>
      </div>

      {}
      {activeTab === 'overview' && (
        <section className="overview-section">
          <h2>Methodology Validation Summary</h2>

          <div className="summary-grid">
            <div className="summary-card">
              <h3>1️⃣ Parameter Optimization</h3>
              <p className="summary-description">
                Grid search with 5-fold cross-validation to justify DBSCAN parameters
              </p>
              <div className="summary-result">
                <strong>Optimal Parameters:</strong> ε={data.gridSearch.optimalEps} days, minPts={data.gridSearch.optimalMinPts}
              </div>
              <div className="summary-metrics">
                Silhouette: {data.gridSearch.optimalSilhouette} | DB Index: {data.gridSearch.optimalDBIndex}
              </div>
              <div className="summary-justification">
                ✓ Semi-annual cycle (6 months) aligns with EDA technology roadmaps
              </div>
            </div>

            <div className="summary-card">
              <h3>2️⃣ Feature Engineering</h3>
              <p className="summary-description">
                Ablation study proving each TechImpactScore component contributes
              </p>
              <div className="summary-result">
                <strong>TechImpactScore Formula:</strong> 35% Patents + 30% DealValue + 20% Age + 15% Activity
              </div>
              {data.featureAblation.results && data.featureAblation.results.length > 0 && (
                <div className="summary-metrics">
                  Baseline Silhouette: {data.featureAblation.results[0].silhouette}
                </div>
              )}
              <div className="summary-justification">
                ✓ Ablation tests confirm each feature improves clustering quality
              </div>
            </div>

            <div className="summary-card">
              <h3>3️⃣ Alternative Algorithms</h3>
              <p className="summary-description">
                Gaussian Mixture Model (GMM) comparison validates DBSCAN choice
              </p>
              <div className="summary-result">
                <strong>GMM (Probabilistic):</strong> Converged in {data.gmmComparison.iterationsToConvergence} iterations
              </div>
              <div className="summary-metrics">
                GMM Silhouette: {data.gmmComparison.silhouette} | DB Index: {data.gmmComparison.dbIndex}
              </div>
              <div className="summary-justification">
                ✓ DBSCAN preferred for density-based wave detection
              </div>
            </div>
          </div>

        </section>
      )}

      {}
      {activeTab === 'gridSearch' && (
        <section className="grid-search-section">
          <h2>1. Parameter Optimization: Grid Search with Cross-Validation</h2>

          <div className="optimal-params-banner">
            <h3>✅ Optimal Parameters Selected</h3>
            <div className="optimal-display">
              <div className="param-box">
                <span className="param-label">ε (Epsilon)</span>
                <span className="param-value">{data.gridSearch.optimalEps} days</span>
              </div>
              <div className="param-box">
                <span className="param-label">MinPts</span>
                <span className="param-value">{data.gridSearch.optimalMinPts}</span>
              </div>
              <div className="param-box">
                <span className="param-label">Silhouette</span>
                <span className="param-value">{data.gridSearch.optimalSilhouette}</span>
              </div>
              <div className="param-box">
                <span className="param-label">DB Index</span>
                <span className="param-value">{data.gridSearch.optimalDBIndex}</span>
              </div>
            </div>
            <p className="execution-time">⏱️ Execution time: {data.gridSearch.executionTimeSeconds}s</p>
          </div>

          <h3>📊 Full Grid Search Results (Top 10 Combinations)</h3>
          <div className="table-container">
            <table className="grid-search-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>ε (days)</th>
                  <th>MinPts</th>
                  <th>Silhouette ↑</th>
                  <th>DB Index ↓</th>
                  <th>Clusters</th>
                  <th>Noise %</th>
                  <th>Domain Justification</th>
                </tr>
              </thead>
              <tbody>
                {data.gridSearch.allCombinations.map((combo) => (
                  <tr key={`${combo.eps}-${combo.minPts}`} className={combo.rank === 1 ? 'optimal-row' : ''}>
                    <td className="rank-cell">{combo.rank === 1 ? '🏆' : combo.rank}</td>
                    <td>{combo.eps}</td>
                    <td>{combo.minPts}</td>
                    <td className="metric-cell">{combo.meanSilhouette} ± {combo.stdSilhouette}</td>
                    <td className="metric-cell">{combo.meanDBIndex}</td>
                    <td>{combo.meanClusters}</td>
                    <td>{combo.noisePercent}%</td>
                    <td className="justification-cell">{combo.justification}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {}
      {activeTab === 'ablation' && (
        <section className="ablation-section">
          <h2>2. Feature Engineering: TechImpactScore Ablation Study</h2>

          <div className="formula-box">
            <h3>✅ Objective TechImpactScore Formula</h3>
            <div className="formula">
              TechImpactScore = (0.35 × PatentScore) + (0.30 × DealValueScore) + (0.20 × CompanyAgeScore) + (0.15 × AcquirerActivityScore)
            </div>
            <p className="formula-note">
              All components normalized using min-max scaling (0-10 range). Patent count weighted highest (35%)
              reflecting EDA's R&D intensity.
            </p>
          </div>

          <h3>📊 Ablation Test Results</h3>
          <div className="table-container">
            <table className="ablation-table">
              <thead>
                <tr>
                  <th>Feature Configuration</th>
                  <th>Silhouette</th>
                  <th>Degradation</th>
                  <th>DB Index</th>
                  <th>Degradation</th>
                  <th>Noise %</th>
                  <th>Clusters</th>
                  <th>Features Included</th>
                </tr>
              </thead>
              <tbody>
                {data.featureAblation.results.map((test, idx) => (
                  <tr key={idx} className={idx === 0 ? 'baseline-row' : ''}>
                    <td className="feature-name-cell">
                      {idx === 0 && '⭐ '}
                      {test.featureSetName}
                    </td>
                    <td className="metric-cell">{test.silhouette}</td>
                    <td className={`degradation-cell ${test.silhouetteDegradation > 0 ? 'degradation-bad' : ''}`}>
                      {idx === 0 ? '—' : `${test.silhouetteDegradation > 0 ? '-' : '+'}${Math.abs(test.silhouetteDegradation).toFixed(1)}%`}
                    </td>
                    <td className="metric-cell">{test.dbIndex}</td>
                    <td className={`degradation-cell ${test.dbIndexDegradation > 0 ? 'degradation-bad' : ''}`}>
                      {idx === 0 ? '—' : `${test.dbIndexDegradation > 0 ? '+' : '-'}${Math.abs(test.dbIndexDegradation).toFixed(1)}%`}
                    </td>
                    <td>{test.noisePercent.toFixed(1)}%</td>
                    <td>{test.numClusters}</td>
                    <td className="features-cell">{test.featuresIncluded}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="interpretation-box">
            <h3>🔍 Interpretation</h3>
            {data.featureAblation.results.map((test, idx) => (
              idx > 0 && (
                <div key={idx} className="interpretation-item">
                  <strong>{test.featureSetName}:</strong> {test.interpretation}
                </div>
              )
            ))}
          </div>
        </section>
      )}

      {/* Algorithm Comparison Tab */}
      {activeTab === 'algorithmComparison' && data.algorithmComparison && (
        <section className="algorithm-comparison-section">
          <h2>3. Comprehensive Algorithm Comparison</h2>

          <div className="optimal-params-banner">
            <h3>🏆 Best Algorithm Selected</h3>
            <div className="optimal-display">
              <div className="param-box">
                <span className="param-label">Algorithm</span>
                <span className="param-value">{data.algorithmComparison.bestAlgorithm}</span>
              </div>
              <div className="param-box">
                <span className="param-label">Silhouette Score</span>
                <span className="param-value">{data.algorithmComparison.bestSilhouette}</span>
              </div>
              <div className="param-box">
                <span className="param-label">Total Algorithms Tested</span>
                <span className="param-value">{data.algorithmComparison.totalAlgorithms}</span>
              </div>
            </div>
            <p className="execution-time">⏱️ Execution time: {data.algorithmComparison.executionTimeSeconds}s</p>
          </div>

          <h3>📊 All Algorithms Ranked by Silhouette Score</h3>
          <div className="table-container">
            <table className="algorithm-comparison-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Algorithm</th>
                  <th>Type</th>
                  <th>Silhouette ↑</th>
                  <th>DB Index ↓</th>
                  <th>Clusters</th>
                  <th>Noise %</th>
                  <th>Characteristics</th>
                </tr>
              </thead>
              <tbody>
                {data.algorithmComparison.results.map((alg) => (
                  <tr key={alg.algorithmName} className={alg.rank === 1 ? 'optimal-row' : ''}>
                    <td className="rank-cell">{alg.rank === 1 ? '🏆' : alg.rank}</td>
                    <td className="algorithm-name-cell">{alg.algorithmName}</td>
                    <td>{alg.algorithmType}</td>
                    <td className={alg.silhouette > 0.5 ? 'excellent-metric' : alg.silhouette > 0 ? 'good-metric' : 'poor-metric'}>
                      {alg.silhouette}
                    </td>
                    <td>{alg.dbIndex}</td>
                    <td>{alg.numClusters}</td>
                    <td>{alg.noisePercent}%</td>
                    <td className="characteristics-cell">{alg.characteristics}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>🔍 Detailed Algorithm Analysis</h3>
          <div className="algorithms-grid">
            {data.algorithmComparison.results.map((alg) => (
              <div key={alg.algorithmName} className={`algorithm-detail-card ${alg.rank === 1 ? 'best-algorithm' : ''}`}>
                <div className="algorithm-header">
                  <h4>{alg.rank === 1 && '🏆 '}{alg.algorithmName}</h4>
                  <span className="algorithm-type-badge">{alg.algorithmType}</span>
                </div>

                <div className="algorithm-metrics">
                  <div className="metric-item">
                    <span className="metric-label">Silhouette:</span>
                    <span className="metric-value">{alg.silhouette}</span>
                  </div>
                  <div className="metric-item">
                    <span className="metric-label">DB Index:</span>
                    <span className="metric-value">{alg.dbIndex}</span>
                  </div>
                  <div className="metric-item">
                    <span className="metric-label">Clusters:</span>
                    <span className="metric-value">{alg.numClusters}</span>
                  </div>
                  <div className="metric-item">
                    <span className="metric-label">Noise:</span>
                    <span className="metric-value">{alg.noisePercent}%</span>
                  </div>
                </div>

                <div className="algorithm-strengths">
                  <h5>✅ Strengths</h5>
                  <pre>{alg.strengths}</pre>
                </div>

                <div className="algorithm-weaknesses">
                  <h5>⚠️ Weaknesses</h5>
                  <pre>{alg.weaknesses}</pre>
                </div>

                <div className="algorithm-reference">
                  <strong>📚 Reference:</strong> {alg.academicReference}
                </div>
              </div>
            ))}
          </div>

          <div className="conclusion-box">
            <h3>🎯 Conclusion: Why {data.algorithmComparison.bestAlgorithm}?</h3>
            <p>
              After systematically testing {data.algorithmComparison.totalAlgorithms} different clustering approaches,
              <strong> {data.algorithmComparison.bestAlgorithm}</strong> achieved the highest Silhouette score
              ({data.algorithmComparison.bestSilhouette}), indicating superior cluster quality for EDA M&A wave detection.
            </p>
            <ul>
              <li><strong>Empirical Evidence:</strong> Tested on {data.datasetSize} M&A transactions (1975-2025)</li>
              <li><strong>Objective Selection:</strong> Chosen based on validation metrics, not researcher bias</li>
              <li><strong>Domain Alignment:</strong> Density-based approach captures consolidation waves better than centroid or probabilistic methods</li>
            </ul>
          </div>
        </section>
      )}

      {/* GMM Comparison Tab */}
      {activeTab === 'gmm' && (
        <section className="gmm-section">
          <h2>4. GMM Details: Probabilistic Soft Clustering</h2>

          <div className="comparison-grid">
            <div className="algorithm-card">
              <h3>🔀 Gaussian Mixture Model (GMM)</h3>
              <p className="algorithm-description">
                Probabilistic soft clustering using Expectation-Maximization (EM) algorithm
              </p>
              <div className="algorithm-metrics">
                <div className="metric-row">
                  <span className="metric-label">Components (k):</span>
                  <span className="metric-value">{data.gmmComparison.numComponents}</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Iterations to Converge:</span>
                  <span className="metric-value">{data.gmmComparison.iterationsToConvergence}</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Converged:</span>
                  <span className={`metric-value ${data.gmmComparison.converged ? 'success' : 'warning'}`}>
                    {data.gmmComparison.converged ? '✅ Yes' : '⚠️ No'}
                  </span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Log Likelihood:</span>
                  <span className="metric-value">{data.gmmComparison.logLikelihood}</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Silhouette Score:</span>
                  <span className="metric-value">{data.gmmComparison.silhouette}</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">DB Index:</span>
                  <span className="metric-value">{data.gmmComparison.dbIndex}</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Execution Time:</span>
                  <span className="metric-value">{data.gmmComparison.executionTimeSeconds}s</span>
                </div>
              </div>
              <div className="algorithm-characteristics">
                <h4>Characteristics:</h4>
                <ul>
                  <li>✓ Soft assignments (probabilistic)</li>
                  <li>✓ Assumes Gaussian distribution</li>
                  <li>✓ Provides confidence scores</li>
                  <li>⚠️ Sensitive to initialization</li>
                </ul>
              </div>
            </div>

            <div className="algorithm-card">
              <h3>🎯 DBSCAN (Reverse Hybrid)</h3>
              <p className="algorithm-description">
                Density-based hard clustering with temporal refinement (K-Means post-processing)
              </p>
              <div className="algorithm-metrics">
                <div className="metric-row">
                  <span className="metric-label">Parameters:</span>
                  <span className="metric-value">ε={data.gridSearch.optimalEps}, minPts={data.gridSearch.optimalMinPts}</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Silhouette Score:</span>
                  <span className="metric-value">{data.gridSearch.optimalSilhouette}</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">DB Index:</span>
                  <span className="metric-value">{data.gridSearch.optimalDBIndex}</span>
                </div>
              </div>
              <div className="algorithm-characteristics">
                <h4>Characteristics:</h4>
                <ul>
                  <li>✓ Hard assignments (deterministic)</li>
                  <li>✓ No distribution assumptions</li>
                  <li>✓ Handles noise explicitly</li>
                  <li>✓ Detects arbitrary shapes</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="comparison-conclusion">
            <h3>📊 Comparison Conclusion</h3>
            <p>
              {data.gmmComparison.silhouette > data.gridSearch.optimalSilhouette
                ? `GMM achieves higher Silhouette (${data.gmmComparison.silhouette} vs ${data.gridSearch.optimalSilhouette}),
                   indicating better-separated probabilistic clusters. However, DBSCAN's explicit noise handling and
                   density-based approach remain more suitable for M&A wave detection (temporal consolidation peaks).`
                : `DBSCAN achieves superior Silhouette (${data.gridSearch.optimalSilhouette} vs ${data.gmmComparison.silhouette}),
                   validating our density-based approach for M&A wave detection. GMM's probabilistic soft assignments
                   are less interpretable for identifying discrete consolidation waves.`}
            </p>
            <p className="conclusion-bold">
              ✅ Methodological Transparency: Testing multiple paradigms (density-based, probabilistic, centroid-based)
              demonstrates rigor and justifies DBSCAN selection empirically.
            </p>
          </div>

          <h3>🔝 Top 10 GMM Assignments (Highest Confidence)</h3>
          <div className="table-container">
            <table className="gmm-assignments-table">
              <thead>
                <tr>
                  <th>M&A ID</th>
                  <th>Cluster</th>
                  <th>Confidence</th>
                  <th>Date</th>
                  <th>TechImpactScore</th>
                </tr>
              </thead>
              <tbody>
                {data.gmmComparison.topAssignments.map((assignment, idx) => (
                  <tr key={idx}>
                    <td>{assignment.maId}</td>
                    <td>Cluster {assignment.clusterId}</td>
                    <td className="confidence-cell">{assignment.confidence}%</td>
                    <td>{assignment.date}</td>
                    <td>{assignment.techScore}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Refresh Button */}
      <div className="action-buttons">
        <button onClick={fetchValidationData} className="refresh-button">
          🔄 Re-run Complete Validation
        </button>
      </div>
    </div>
  );
};

export default MethodologyValidationDashboard;
